import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';

interface FeedbackSectionTextareaProps {
  id?: string;
  autoFocus?: boolean;
  errorMessage?: string;
  onCancel?: () => unknown;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  placeholder: string;
  initialValue?: string;
  onSave: (value: string) => unknown;
}

const StyledDiv = styled.div`
  border-radius: 4px;
  border: 1px solid ${COLORS.grayLight};
  background-color: ${COLORS.whiteBase};
  textarea {
    border: none;
    outline: none;
    width: 100%;
    resize: vertical;
    min-height: 100px;
    padding: ${SPACERS.small};
  }
  .textarea-buttons {
    padding: 14px ${SPACERS.medium};
    background-color: ${COLORS.grayLight};
    .error-message {
      margin-bottom: 14px;
    }
    .buttons {
      display: flex;
      .save-button {
        margin-right: ${SPACERS.tiny};
      }
      .clear-button {
        margin-left: ${SPACERS.tiny};
      }
    }
  }
`;

const FeedbackSectionTextarea: React.FC<FeedbackSectionTextareaProps> = ({
  id,
  autoFocus,
  errorMessage,
  placeholder,
  initialValue,
  onFocus,
  onCancel,
  onSave,
}) => {
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState<string>(initialValue || '');

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const position = initialValue?.length;
      position && inputRef.current.setSelectionRange(position, position);
    }
  }, [autoFocus, inputRef.current]);

  const handleSave = useCallback(() => {
    if (!value) return;
    onSave(value);
    setValue('');
  }, [value, onSave, setValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      setValue(value);
    },
    [setValue],
  );

  const onClear = () => setValue('');
  return (
    <StyledDiv>
      <textarea
        ref={inputRef}
        defaultValue={initialValue}
        onFocus={onFocus}
        autoFocus={autoFocus}
        value={value}
        onChange={handleChange}
        id={id}
        placeholder={placeholder}
      />
      <div className="textarea-buttons">
        {errorMessage && (
          <TrueImpactText color="critical" className="error-message">
            {errorMessage}
          </TrueImpactText>
        )}
        <div className="buttons">
          <TrueImpactButton
            disabled={!value}
            variant="primary-large"
            className="save-button"
            onClick={handleSave}
          >
            Save
          </TrueImpactButton>
          <TrueImpactButton
            variant="secondary-large"
            className="clear-button"
            onClick={onCancel ? onCancel : onClear}
          >
            {onCancel ? 'Cancel' : 'Clear'}
          </TrueImpactButton>
        </div>
      </div>
    </StyledDiv>
  );
};

export default FeedbackSectionTextarea;
